import { useEffect, useMemo, useState } from 'react';

import {
  FillInBlanksAnswer,
  FillInBlanksResponseAnswer,
  FillInBlanksSentence as FillInBlanksSentenceType,
  ViewProps,
} from './fill-in-blanks.constants';
import {
  useRichtext,
  useRichTextAddAria,
} from 'approot/shared/richtext/richtext.hooks';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { getInitialContent } from 'approot/shared/richtext/get-initial-richtext-data';
import { useActivityAnswerId } from 'approot/activity-builder/present/activity-answers.hooks';
import { AnswerInput } from '../shared/batch-answers.context';
import type { AnswerFragment } from '../../present/activity-answers.graphql.generated';
import { ACTIVITIES_ANSWER_TEXT } from 'global/constants';
import { sortFillInBlanksSentences } from './logic/sort-fill-in-blanks-sentences';
import { mergeFillInBlanksBlocks } from './logic/merge-fill-in-blanks-blocks';
import { getFillInBlanksAnswerColour } from './logic/get-fill-in-blanks-answer-colour';
import { getFillInBlanksRichtext } from './logic/get-fill-in-blanks-richtext';
import { getFillInBlanksSentenceAnswer } from './logic/get-fill-in-blanks-sentence-answer';
import { useFillInBlanksRichtextSettings } from './logic/use-fill-in-blanks-richtext-settings';
import { useFillInBlanksEditorWidth } from './logic/use-fill-in-blanks-editor-width';
import {
  FillInBlanksSentence,
  FillInBlanksSentencesWrapper,
} from './fill-in-blanks-shared.style';
import * as Styled from './fill-in-blanks-view.style';
import FillInBlanksTextBlock from './fill-in-blanks-text-block';

const AnswerBlock = ({
  activityId,
  componentId,
  answerBlock,
  fieldAnswer,
  fontSize,
  fontFamily,
  updateAnswer,
  onFocus,
}: {
  activityId: string;
  componentId: string;
  answerBlock: FillInBlanksAnswer;
  fieldAnswer: FillInBlanksResponseAnswer | null | undefined;
  fontSize: string | null;
  fontFamily: string | null;
  updateAnswer: (input: AnswerInput) => void;
  onFocus?: (id: string) => void;
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const id = useActivityAnswerId(fieldAnswer);

  const colours = useMemo(
    () => getFillInBlanksAnswerColour(answerBlock),
    [answerBlock]
  );
  const shouldShowLabel = useMemo(
    () => !!(answerBlock.showLabel && answerBlock.label),
    [answerBlock]
  );

  const settings = useFillInBlanksRichtextSettings(answerBlock);
  const { hasTextOverflowed, editorRef, setEditorRef, adjustFieldState } =
    useFillInBlanksEditorWidth({
      answer: answerBlock,
      fontSize,
      fontFamily,
    });

  const { editor } = useRichtext({
    initialValue: getInitialContent({
      initialValue: getFillInBlanksRichtext(fieldAnswer),
      settings,
    }),
    onChange: json => {
      updateAnswer({
        __typename: 'ActivityAnswer',
        uuid: id,
        activityId,
        componentId,
        answerType: ACTIVITIES_ANSWER_TEXT,
        createdAt: new Date(),
        updatedAt: null,
        studentId: null,
        answer: {
          __typename: 'RichtextAnswer',
          answerType: ACTIVITIES_ANSWER_TEXT,
          richtext: json,
        },
        answerReferences: {
          __typename: 'ReferenceProperties',
          id: answerBlock.itemId,
          row: null,
          col: null,
        },
      });
    },
    onFocus: () => {
      setIsEditing(true);
      onFocus?.(componentId);
    },
    onBlur: () => setIsEditing(false),
    settings,
  });

  useRichTextAddAria({
    ariaLabelledBy: `fib-${answerBlock.itemId}`,
    ref: editorRef,
    role: 'textbox',
  });

  useEffect(() => {
    // answers come in separately, only initialise once
    // whilst not editing
    if (fieldAnswer && !isEditing) {
      const editorContent = getInitialContent({
        initialValue: getFillInBlanksRichtext(fieldAnswer),
        settings,
      });
      editor?.commands.setContent(
        editorContent
          ? editorContent
          : {
              type: 'doc',
              content: [],
            }
      );

      if (editorRef) {
        adjustFieldState(editorRef);
      }
    }
  }, [fieldAnswer, isEditing, editor, settings, editorRef, adjustFieldState]);

  return (
    <Styled.FillInBlanksField
      $colours={colours}
      $minWidth={answerBlock.width}
      $hasTextOverflowed={hasTextOverflowed}
      $punctuation={answerBlock.punctuation}
      $hasLabel={shouldShowLabel}
      $fontSize={fontSize}
      $fontFamily={fontFamily}
    >
      <ScreenReaderOnly id={`fib-${answerBlock.itemId}`}>
        {`Fill in the Blanks field: ${
          shouldShowLabel
            ? answerBlock.label
            : answerBlock.placeholder
              ? answerBlock.placeholder
              : ''
        }`}
      </ScreenReaderOnly>
      {editor && (
        <Styled.FillInBlanksAnswer
          ref={setEditorRef}
          editor={editor}
          $colours={colours}
          $label={answerBlock.showLabel ? answerBlock.label : null}
          $fontSize={fontSize}
          $fontFamily={fontFamily}
        />
      )}
    </Styled.FillInBlanksField>
  );
};

const Sentence = ({
  sentence,
  fieldAnswers,
  activityId,
  componentId,
  updateAnswer,
  onFocus,
  fontSize,
  fontFamily,
}: {
  sentence: FillInBlanksSentenceType;
  fieldAnswers?: AnswerFragment[];
  activityId: string;
  componentId: string;
  fontSize: string | null;
  fontFamily: string | null;
  updateAnswer: (input: AnswerInput) => void;
  onFocus?: (id: string) => void;
}) => {
  const mergedBlocks = useMemo(
    () => mergeFillInBlanksBlocks(sentence),
    [sentence]
  );

  const fieldAnswerMap = useMemo(
    () => getFillInBlanksSentenceAnswer(sentence, fieldAnswers),
    [sentence, fieldAnswers]
  );

  return mergedBlocks.map(block => {
    return 'text' in block ? (
      <FillInBlanksTextBlock key={block.itemId} textBlock={block} />
    ) : (
      <AnswerBlock
        key={`${block.itemId}-${block.showPlaceholderAsScaffold}-${block.placeholder}`}
        answerBlock={block}
        fieldAnswer={fieldAnswerMap[block.itemId]}
        activityId={activityId}
        componentId={componentId}
        fontSize={fontSize}
        fontFamily={fontFamily}
        updateAnswer={updateAnswer}
        onFocus={onFocus}
      />
    );
  });
};

const FillInBlanksViewContent = ({
  id,
  properties,
  subActivityProperties,
  componentAnswers,
  updateAnswer,
  onFocus,
}: ViewProps & {
  componentAnswers?: AnswerFragment[];
  updateAnswer: (input: AnswerInput) => void;
  onFocus?: (id: string) => void;
}) => {
  const sortedSentences = useMemo(
    () => sortFillInBlanksSentences(properties.fillInBlanks.sentences),
    [properties.fillInBlanks.sentences]
  );

  return (
    <FillInBlanksSentencesWrapper
      $fontFamily={properties.fillInBlanks.fontFamily}
      $fontSize={properties.fillInBlanks.fontSize}
    >
      {sortedSentences.map(sentence => (
        <FillInBlanksSentence key={sentence.itemId}>
          <Sentence
            sentence={sentence}
            fieldAnswers={componentAnswers}
            activityId={subActivityProperties?.activityComponentId!}
            componentId={id}
            updateAnswer={updateAnswer}
            onFocus={onFocus}
            fontSize={properties.fillInBlanks.fontSize}
            fontFamily={properties.fillInBlanks.fontFamily}
          />
        </FillInBlanksSentence>
      ))}
    </FillInBlanksSentencesWrapper>
  );
};

export default FillInBlanksViewContent;
